import _, { uniqueId } from 'lodash';
import dayjs from 'dayjs';
import { OptionType, AssetType, OptionExpirationType, OrderStatuses, } from '@tradingblock/types';
import { OrderAction, PositionEffect, OrderActionDirection, OrderClass, Durations as DurationValues, Durations, DebitOrCredit, } from '@tradingblock/types';
import { isIndexSymbol, parseOccSymbol, isOptionAsset } from './SymbolUtilities';
import { Strategies } from '../strategies/strategies';
import { CustomStrategy } from '../strategies/CustomStrategy';
import { isBetweenInclusive } from './DateUtilities';
export const OCCSymbolToOptionData = parseOccSymbol;
export const DeriveLegAssetType = (leg) => {
    if (_.isNil(leg.OptionType) === false) {
        return AssetType.Option;
    }
    else if (isIndexSymbol(leg.UnderlyingSymbol)) {
        return AssetType.Index;
    }
    else {
        return AssetType.Equity;
    }
};
export const GetOCCSymbol = (symbolData) => {
    const { underlyingSymbol, expiration, optionType, strike, prefixSymbolName } = symbolData;
    if (expiration === undefined || strike === undefined || optionType === undefined) {
        return underlyingSymbol;
    }
    const otype = optionType === OptionType.Put ? 'P' : 'C';
    let rootSymbol = underlyingSymbol;
    rootSymbol = _.replace(underlyingSymbol, '$', '');
    const doNotAddWeek = ['NANOS', 'XSP'];
    if (isIndexSymbol(underlyingSymbol)) {
        // rootSymbol = _.replace(underlyingSymbol, '$', '');
        if (expiration.optionExpirationType === OptionExpirationType.PM && !doNotAddWeek.includes(rootSymbol)) {
            rootSymbol = `${rootSymbol}W`;
        }
    }
    const dateVal = dayjs(expiration.date).format('YYMMDD');
    const strikeVal = strike * 1000;
    const prefix = prefixSymbolName ? '.' : '';
    return `${prefix}${rootSymbol} ${dateVal}${otype}${strikeVal}`;
};
export const orderLegOccSymbol = (underlyingSymbol, leg) => {
    const { Expiration, OptionType, Strike } = leg;
    let symbolOcc = undefined;
    if (!OptionType || leg.AssetType === AssetType.Equity) {
        symbolOcc = underlyingSymbol.symbol;
    }
    else if (OptionType && Expiration && Strike) {
        symbolOcc = GetOCCSymbol({
            underlyingSymbol: underlyingSymbol.symbol,
            symbolType: underlyingSymbol.type,
            expiration: Expiration,
            optionType: OptionType,
            strike: Strike,
        });
    }
    else {
        symbolOcc = undefined;
    }
    return symbolOcc;
};
export const toPrefixedOccSymbol = (symbol) => (_.first(symbol) === '.' ? symbol : `.${symbol}`);
export const toRawOccSymbol = (symbol) => _.isNil(symbol) ? '' : _.first(symbol) === '.' ? _.replace(symbol, '.', '') : symbol;
export const AllStrategies = [...Strategies, CustomStrategy];
export const getStrategy = (name) => _.find(AllStrategies, s => s.info.Name === name) || CustomStrategy;
export const getKnownStrategy = (name) => _.find(Strategies, s => s.info.Name === name);
export const buildOrderLeg = (partOrd, orderAction, assetType, positionEffect, optionType, ratio) => {
    return {
        Symbol: partOrd.Symbol,
        UnderlyingSymbol: partOrd.UnderlyingSymbol || partOrd.Symbol,
        Id: uniqueId(),
        SpreadRatio: ratio,
        AssetType: assetType,
        Action: orderAction,
        PositionEffect: positionEffect,
        OptionType: optionType,
    };
};
export const transformForApi = (orderDesc) => {
    let transformedOrder = orderDesc.kind === 'simple'
        ? {
            AccountId: orderDesc.accountId,
            SubaccountId: orderDesc.subaccountId,
            UnderlyingSymbol: '',
            Legs: [
                {
                    AssetType: orderDesc.assetType,
                    Symbol: orderDesc.symbol,
                    Action: orderDesc.action,
                    PositionEffect: orderDesc.effect,
                },
            ],
            ClientRefId: orderDesc.referenceId,
            OrderClass: OrderClass.Single,
            Quantity: orderDesc.quantity,
            OrderType: orderDesc.orderType,
            Price: orderDesc.price,
            Stop: orderDesc.stop,
            AllOrNone: false,
            Duration: DurationValues.Day,
            Description: '',
            DebitCredit: DebitOrCredit.Debit,
        }
        : Object.assign({}, orderDesc, { Legs: _.map(orderDesc.Legs, l => {
                return Object.assign({}, l, { Symbol: l.OccSymbol || l.Symbol });
            }) });
    const isSpreadOrder = _.keys(transformedOrder.Legs).length > 1 ? true : false;
    const DebitCredit = isSpreadOrder ? transformedOrder.DebitCredit : undefined;
    const finalorder = Object.assign({}, transformedOrder, { DebitCredit });
    return transformOrderForApi(finalorder);
};
const getCallPutValue = (l) => {
    const legSymbol = l.OccSymbol;
    if (legSymbol === undefined) {
        return null;
    }
    const symInfo = parseOccSymbol(legSymbol);
    if (isOptionAsset(symInfo)) {
        return symInfo.option === OptionType.Call ? true : false;
    }
    return null;
};
function isPartialOrderWithLegs(order) {
    const ordLegs = _.get(order, 'Legs', undefined);
    return order && _.isArray(ordLegs) ? true : false;
}
export function transformOrderForApi(order) {
    const Price = Math.abs(order.Price);
    if (isPartialOrderWithLegs(order)) {
        const Legs = _.map(order.Legs, l => {
            return Object.assign({}, l, { CallPut: getCallPutValue(l) });
        });
        return Object.assign({}, order, { Price,
            Legs });
    }
    return Object.assign({}, order, { Price });
}
export const transformFromApi = (order) => {
    const Duration = getDurationFromCode(order.Duration);
    return Object.assign({}, order, { Duration, Legs: _.map(order.Legs, l => {
            return Object.assign({}, l, { Symbol: toRawOccSymbol(l.Symbol), OccSymbol: toPrefixedOccSymbol(l.Symbol) });
        }) });
};
const getDurationFromCode = (duration) => {
    if (_.isNumber(duration)) {
        const durationCode = _.toNumber(duration);
        switch (durationCode) {
            case 1:
                return Durations.Day;
            case 2:
                return Durations.GTC;
            case 3:
                return Durations.Manual;
        }
    }
    return duration;
};
export const BuildInitialOrder = (strategy, partOrd) => ({
    OrderClass: strategy.profile.OrderClass,
    Legs: _.map(strategy.profile.Legs, (leg, ind) => {
        let legAction = leg.Direction === OrderActionDirection.Long ? OrderAction.Buy : OrderAction.Sell;
        if (leg.Direction === undefined) {
            legAction = ind === 0 ? OrderAction.Buy : OrderAction.Sell;
        }
        return Object.assign({}, leg, buildOrderLeg(partOrd, legAction, leg.AssetType, PositionEffect.Open, leg.OptionType, leg.SpreadRatio));
    }),
});
export const getNextLegId = (existingLegs) => {
    let id = _.uniqueId('leg_');
    while (_.keys(existingLegs).includes(id)) {
        id = _.uniqueId('leg_');
    }
    return id;
};
export const orderToReplaceOrder = (order) => {
    const valOrUndef = (val) => (val && val !== 0 ? val : undefined);
    return {
        AccountId: order.AccountId,
        DebitCredit: order.DebitCredit,
        SubaccountId: valOrUndef(order.SubaccountId),
        OrderId: order.OrderId,
        ClientRefId: valOrUndef(order.ClientRefId),
        OrderType: order.OrderType,
        Price: order.Price,
        Stop: valOrUndef(order.Stop),
    };
};
export const OnlyFilledOrders = (orders) => {
    return _(orders)
        .filter(o => {
        const orderStatus = o.OrderStatus;
        switch (orderStatus) {
            case OrderStatuses.PartiallyFilled:
            case OrderStatuses.Filled: {
                return true;
            }
            default:
                return false;
        }
    })
        .value();
};
export const FilterOrderByDate = (orders, startDate, endDate) => {
    return _.filter(orders, (order) => {
        if (startDate === undefined) {
            return true;
        }
        const endDateVal = endDate || new Date();
        return isBetweenInclusive(order.Date, startDate, endDateVal) ? true : false;
    });
};
export const TodaysFilledOrders = (orders) => {
    const startDate = dayjs(new Date())
        .hour(0)
        .toDate();
    const endDate = dayjs(new Date()).toDate();
    return OnlyFilledOrders(FilterOrderByDate(orders, startDate, endDate));
};
export const GetSymbolsFromOrders = (orders) => {
    return _.reduce(orders, (acc, o) => {
        const { Legs } = o;
        const orderSymbols = Legs ? _.map(Legs, l => l.Symbol) : [];
        const updated = _(acc)
            .union(orderSymbols)
            .uniq()
            .value();
        return updated;
    }, []);
};
const toOrderLegWithMetaData = (o, l) => {
    return Object.assign({}, l, { UnderlyingSymbol: o.UnderlyingSymbol, OrderId: o.OrderId, Date: o.Date });
};
const toOrderLegsWithMetaData = (orders) => {
    return _.flatMap(orders, o => o.Legs.map(l => toOrderLegWithMetaData(o, l)));
};
export const orderutility = {
    toOrderLegsWithMetaData: toOrderLegsWithMetaData,
};
export const isSymbolExpired = (symbol) => {
    const symbData = parseOccSymbol(symbol);
    //not expired
    if (symbData.expiration && symbData.expiration.valueOf() <= new Date().valueOf()) {
        return true;
    }
    return false;
};
export const getDefaultOrderQuantity = (strategy, { defaultQuantityForOptionsAndStrategies, defaultQuantityForShares }) => {
    if (strategy === 'Custom' || strategy === undefined) {
        return 1;
    }
    else if (strategy === 'Shares') {
        return defaultQuantityForShares || 1;
    }
    else {
        return defaultQuantityForOptionsAndStrategies || 1;
    }
};
export const getCustomStrategyLegQuantity = (assetType, { defaultQuantityForOptionsAndStrategies, defaultQuantityForShares }) => {
    if (assetType === AssetType.Equity) {
        return defaultQuantityForShares || 1;
    }
    else {
        return defaultQuantityForOptionsAndStrategies || 1;
    }
};
export const findDuplicateLegs = (legs) => {
    const partialLegs = _.map(legs, l => {
        const symbolData = parseOccSymbol(l.Symbol);
        const data = {
            spreadRatio: l.SpreadRatio,
            expiration: symbolData.expiration,
            strike: symbolData.strike,
            optionType: symbolData.option,
        };
        return {
            symbol: l.Symbol,
            occSymbol: l.OccSymbol,
            data,
        };
    });
    return _.filter(partialLegs, (curr, currInd) => {
        var eq = _.find(partialLegs, (e, eInd) => _.isEqual(e.data, curr.data) && eInd !== currInd);
        return eq ? true : false;
    });
};
//# sourceMappingURL=OrderUtilities.js.map